import { AsgardDocBase } from './asgard-doc-base';
import { HtmlTextIterator } from '../reading-utils/html-text-iterator';
import { ReadingUnitType } from '../reading-utils/enums';
import { ReadingAtom } from '../reading-utils/interfaces';
import { HighlightingTypes } from '../pdf-utils/enums';
import { ILanguage } from '../interfaces';
import { IAutoSaveDocChanges } from 'src/app/services/autosave/autosave.interface';
import { getCaretPostion } from '../html-and-text-utils';
import { generateCustomDocumentName, isDocHashCustomDoc } from '../common';

export class AsgardDocHtml extends AsgardDocBase {
  protected textIteratorBuffer: HtmlTextIterator | null = null;
  private isDocReady: boolean = false;

  constructor(private readonly editorCls: string,
    private readonly isIFrame: boolean,
    public readonly isStaticContent: boolean,
    docId: string) {
    super(docId);
  }

  get editorQueryStr(): string {
    return `.${this.editorCls}[editorid="${this.documentId}"]`;
  }

  protected get editorSelection(): Selection | null {
    return this.editorWin ? this.editorWin.getSelection() : null;
  }

  get isDocumentReady(): boolean {
    return this.isDocReady;
  }

  set isEditable(value: boolean) {
    if (!this.isStaticContent && this.editorElement) {
      this.editorElement.setAttribute('contenteditable', value.toString());
    }
  }

  get isEditable(): boolean {
    return this.isStaticContent ? false : !!this.editorElement && this.editorElement.getAttribute('contenteditable') === 'true';
  }

  get editorFrame(): HTMLIFrameElement | null {
    if (this.isIFrame) {
      const editorQuery = `${this.editorQueryStr} iframe`;
      const frame = window.document.querySelector(editorQuery) as HTMLIFrameElement;
      return frame ? frame : null;
    }

    return null;
  }


  get editorElement(): HTMLElement | null {
    if (this.isIFrame) {
      const editorQuery = `${this.editorQueryStr} iframe`;
      if (window.document.querySelector(editorQuery) !== null) {
        const frameDoc = (window.document.querySelector(editorQuery) as HTMLIFrameElement).contentDocument;

        if (frameDoc) {
          return frameDoc.querySelector('[contenteditable]');
        }
      }
    }
    return window.document.querySelector(this.editorQueryStr);
  }

  get editorWin(): Window | null {
    if (this.isIFrame) {
      const editorQuery = `${this.editorQueryStr} iframe`;
      return (window.document.querySelector(editorQuery) as HTMLIFrameElement).contentWindow;
    }

    return window;
  }

  get scrollContainer(): HTMLElement | null {
    if (this.isIFrame) {
      const editorQuery = `${this.editorQueryStr} iframe`;
      return (window.document.querySelector(editorQuery) as HTMLIFrameElement).parentElement;
    }

    return window.document.querySelector(this.editorQueryStr);
  }

  protected get textIterator(): HtmlTextIterator {
    if (this.textIteratorBuffer === null) {
      this.textIteratorBuffer = new HtmlTextIterator(this.editorSelection, this.isIFrame ? null : this.editorQueryStr);
    }

    return this.textIteratorBuffer;
  }

  async selectReadingUnitByClick(_evt: MouseEvent, unit: ReadingUnitType): Promise<void> {
    this.setReadingItem(this.textIterator.getClosestItemToCursor(unit), true);
  }

  async goToNextReadingItem(itemType: ReadingUnitType): Promise<void> {
    if (this.hasNextItemToRead(itemType)) {
      this.setReadingItem(this.textIterator.getNextItem(itemType));
    }
  }

  async goToPrevReadingItem(itemType: ReadingUnitType): Promise<void> {
    if (this.hasPrevItemToRead()) {
      this.setReadingItem(this.textIterator.getPrevItem(itemType));
    }
  }

  async goToFirstItem(itemType: ReadingUnitType): Promise<void> {
    this.setReadingItem(this.textIterator.getFirstItem(itemType));
  }

  async goToLastItem(itemType: ReadingUnitType): Promise<void> {
    this.setReadingItem(this.textIterator.getLastItem(itemType));
  }

  async startNextItemFrom(itemType: ReadingUnitType): Promise<void> {
    if (itemType === ReadingUnitType.Word) {
      this.goToNextReadingItem(itemType);
    } else if (itemType === ReadingUnitType.Sentence) {
      if (this.readingItem && this.readingItem[0]) {
        this.setReadingItem(this.textIterator.getNextSentenceFrom(this.readingItem[0].itemIdx));
      }
    }
  }

  async doHighlight(words: ReadingAtom[], color: string, itemType: HighlightingTypes, exclusive: boolean = true): Promise<void> {
    this.beforeHighlight(words, color, itemType, exclusive);

    this.currentHighlight.forEach(x => {
      const dstRange = this.textIterator.getRangeForWords(x.words);

      if (this.textIterator.actvSlctn) {
        this.textIterator.actvSlctn.removeAllRanges();
        this.textIterator.actvSlctn.addRange(dstRange as Range);
      }

      this.execHtmlDocumentCommand('backColor', x.color);
      x.active = true;

    });

    if (this.textIterator.actvSlctn) {
      this.textIterator.actvSlctn.collapseToStart();
    }
  }

  async selectHoverWord(e: MouseEvent): Promise<void> {
    if (e.clientX < 0 || e.clientY < 0) {
      return;
    }

    const item = this.textIterator.getWordByPoint(e.clientX, e.clientY);
    // ASG-1158 for fire stopOnCurrentItem in reading service,
    // always return last word on hover reading mode
    if (item && item.length) {
      item[0].isLast = true;
    }

    this.setReadingItem(item);
  }

  appendText(text: string, owerwrite: boolean = true): void {
    let currentRange = this.textIterator.actvSlctn ? this.textIterator.actvSlctn.getRangeAt(0) : null;

    if (currentRange === null) {
      if (this.textIterator.actvDocument) {
        currentRange = this.textIterator.actvDocument.createRange();

        if (this.textIterator.actvSlctn) {
          this.textIterator.actvSlctn.addRange(currentRange);
        }
      }
    } else if (owerwrite) {
      currentRange.deleteContents();
    }

    const txtByParagraphs = text.split(/\n|\r/);

    for (let i = 0; i < txtByParagraphs.length; i++) {
      if (txtByParagraphs[i].length > 0) {
        this.addTextNode(txtByParagraphs[i], currentRange as Range);
      } else if (currentRange && this.textIterator.actvDocument) {
        currentRange.insertNode(this.textIterator.actvDocument.createElement('br'));
      }

      if (i > 0 && currentRange && this.textIterator.actvDocument) {
        currentRange.insertNode(this.textIterator.actvDocument.createElement('br'));
      }
    }

    if (this.textIterator.actvSlctn) {
      this.textIterator.actvSlctn.collapseToEnd();
    }

    this.onDocumentChanged();
  }

  clearTextHighlight(force: boolean = false, clicked: boolean = false): void {
    if (this.currentHighlight.length > 0) {
      const words = this.textIterator.getItemUnderCursor(ReadingUnitType.Word, false);
      let currentRange = (this.textIterator.actvSlctn && this.textIterator.actvSlctn.rangeCount > 0) ? this.textIterator.actvSlctn.getRangeAt(0) : null;

      // if document has no focus or has wrong offset
      const anchorNode = this.textIterator.actvSlctn ? this.textIterator.actvSlctn.anchorNode : null;
      if (this.editorElement && anchorNode && (!this.editorElement.contains(anchorNode) ||
        anchorNode.textContent && this.textIterator.actvSlctn &&
        anchorNode.textContent.length === this.textIterator.actvSlctn.anchorOffset)) {
        this.editorElement.focus();

        if (this.textIterator.actvDocument && this.textIterator.actvSlctn && this.editorElement.firstChild) {
          currentRange = this.textIterator.actvDocument.createRange();
          currentRange.setStart(this.editorElement.firstChild, 0);
          currentRange.setEnd(this.editorElement.firstChild, 0);
          this.textIterator.actvSlctn.removeAllRanges();
          this.textIterator.actvSlctn.addRange(currentRange);
        }
      }

      this.currentHighlight.forEach(x => {
        if (x.active) {
          this.execHtmlDocumentCommand('undo');
          x.active = false;
        }
      });

      // need to preserve current mouse position
      if (clicked && words !== null && words.length > 0) {
        currentRange = this.textIterator.getRangeForWords(words);
      }

      if (this.textIterator.actvSlctn) {
        this.textIterator.actvSlctn.removeAllRanges();
        this.textIterator.actvSlctn.addRange(currentRange as Range);
        this.textIterator.actvSlctn.collapseToStart();
      }

      super.clearTextHighlight(force);
    }
  }

  setDocumentOptins(lngMpr: (...args) => ILanguage, ...optArgs): void {
    this.isDocReady = true;
    super.setDocumentOptins(lngMpr, optArgs);
  }

  async setSelectionToReadingItem(): Promise<void> {
    const newReadingItem = this.textIterator.getWordsFromSelection();
    if (newReadingItem !== null && newReadingItem.length > 0) {
      this.setReadingItem(newReadingItem, false);
    }
  }

  hasSelection(): boolean {
    const sel = this.editorWin ? this.editorWin.getSelection() : null;

    return sel ? !sel.isCollapsed : false;
  }

  getCurrentPageIdx(): number {
    return 0;
  }

  selectedText(): string {
    return this.editorSelection ? this.editorSelection.toString() : '';
  }

  hasNextItemToRead(_itemType: ReadingUnitType): boolean {
    const lastItem = this.readingItem && this.readingItem[this.readingItem.length - 1];

    return this.hasReadingItem && !!lastItem && !lastItem.isLast;
  }

  hasPrevItemToRead(): boolean {
    const item = this.readingItem && this.readingItem[0];

    return this.hasReadingItem && !!item && item.itemIdx !== 0;
  }

  getItemUnderCursor(unit: ReadingUnitType): ReadingAtom[] | null {
    return this.textIterator.getItemUnderCursor(unit);
  }

  getItemBeforeCursor(unit: ReadingUnitType): ReadingAtom[] | null {
    return this.textIterator.getItemBeforeCursor(unit);
  }

  getShowedDocRect(): { x1: number, y1: number, x2: number, y2: number } | null {
    const wrapper = this.scrollContainer;

    if (wrapper) {
      return {
        x1: wrapper.scrollLeft,
        y1: wrapper.scrollTop,
        x2: wrapper.scrollLeft + wrapper.clientWidth,
        y2: wrapper.scrollTop + wrapper.clientHeight,
      };
    }

    return null;
  }

  scrollToWord(word: ReadingAtom): void {
    if (this.scrollContainer === null) {
      return;
    }

    if (word && word.itemIdx >= 0) {
      const nodes = this.textIterator.getReadingItemNode(word.itemIdx);

      if (nodes && nodes.length > 0) {
        const itemNode = this.textIterator && this.textIterator.getReadingItemNode(word.itemIdx);
        const wordNode = itemNode && itemNode[0] && itemNode[0].wordNode;
        const wordRect = wordNode && (wordNode.parentNode as HTMLElement).getBoundingClientRect();

        if (wordRect === null) {
          return;
        }

        const wrapRect = this.scrollContainer && this.scrollContainer.getBoundingClientRect();
        const wordPos = wordRect.bottom - (this.isIFrame ? this.scrollContainer.scrollTop : wrapRect.top);

        if (wordPos + wordRect.height > this.scrollContainer.clientHeight || wordPos - wordRect.height < 0) {
          const offset = this.isIFrame ? wordRect.top : this.scrollContainer.scrollTop + (wordRect.top - wrapRect.top);
          this.scrollContainer.scrollTo({ behavior: 'smooth', top: offset });
        }
      }
    }
  }

  onDocumentChanged(): void {
    this.textIteratorBuffer = null;
  }

  clearReadingItem(): void {
    this.setReadingItem(null);
  }

  protected execHtmlDocumentCommand(cmd: string, arg?: string): void {
    if (this.editorElement && this.editorWin) {
      this.editorElement.setAttribute('contentEditable', 'true');
      this.editorWin.document.execCommand(cmd, false, arg);
      if (!this.isEditable) {
        this.editorElement.setAttribute('contentEditable', 'false');
      }
    }
  }

  protected setReadingItem(words: ReadingAtom[] | null, clicked: boolean = false): void {
    if (words === null || words.length === 0) {
      super.setReadingItem(words as ReadingAtom[], clicked);
      return;
    }

    words.forEach(x => x.lang = this.textIterator.getWordLang(x.itemIdx));
    super.setReadingItem(words, clicked);
  }

  async getDocumentUnsavedChanged(): Promise<IAutoSaveDocChanges> {
    let data: IAutoSaveDocChanges | null = null;
    let getContent = isDocHashCustomDoc(this.docHash as string);
    data = {
      unsavedChanges: this.currentValue(),
      documentHash: this.docHash as string,
      fileName: getContent ? generateCustomDocumentName(this.editorElement ? this.editorElement.innerText : '') : null,
      readingItemIdx: this.getReadingPostition(),
      readingItemPageIdx: null,
      pageIdx: null
    };
    return data;
  }

  public currentValue(): string {
    return this.editorElement ? this.editorElement.innerHTML : '';
  }

  public getCursorPostion(): number {
    let caretPos = 0;
    const pos = getCaretPostion(this.editorElement, this.editorWin);
    caretPos = pos ? pos.start : 0;
    return caretPos;
  }

  public redoSymbol(offset: number, value: string): void {
    if (this.editorElement === null) {
      return;
    }

    const text = this.editorElement.innerText;
    this.editorElement.innerText = text.substr(0, offset) + value + text.substr(offset);
  }

  public undoSymbol(offset: number): void {
    if (this.editorElement === null) {
      return;
    }

    const text = this.editorElement.innerText;
    this.editorElement.innerText = text.substring(0, offset - 1) + text.substring(offset, text.length);
  }

  protected addTextNode(text: string, dstRange: Range): void {
    if (this.textIterator.actvDocument === null) {
      return;
    }

    if (text === ' ') {
      dstRange.insertNode(this.textIterator.actvDocument.createTextNode(' '));
      return;
    }

    const textContent = this.textIterator && this.textIterator.actvSlctn &&
      this.textIterator.actvSlctn.focusNode && this.textIterator.actvSlctn.focusNode.textContent;
    const beginWithSpace = text.substring(0, 1).trim().length === 0;
    const docHasFinalSpace = textContent && textContent.substring(textContent.length - 1, 1);

    if (textContent && textContent.trim().length > 0 && !beginWithSpace && !docHasFinalSpace) {
      text = ' ' + text;
    }

    dstRange.insertNode(this.textIterator.actvDocument.createTextNode(text));
  }

  async lastReadingPositionFounded(_pageIdx: number | null, readingItemIdx: number | null,
    _readingItemPageIdx: number | null, itemType: ReadingUnitType): Promise<void> {
    if (readingItemIdx !== null) {
      const items = this.textIterator.getNextSentenceFrom(readingItemIdx);

      if (items && items.length !== 0) {
        this.scrollToWord(items[0]);
        this.setReadingItem(itemType === ReadingUnitType.Sentence ? items : [items[0]]);
      }
    }
  }

}
